<template>
  <LightBlockButton @click="contactInfoModal?.$el?.present()" size="small">
    <i class="mr-2 text-blue-500" :class="mode === 'add' ? 'icon-contact-card' : 'icon-edit'"></i>
    <span>{{ mode === 'add' ? 'Add' : 'Edit' }} Info</span>
  </LightBlockButton>
  <IonModal ref="contactInfoModal">
    <IonHeader class="ion-no-border px-[10px]">
      <IonToolbar color="white" class="border-b-2 border-gray-300">
        <IonTitle color="medium"><i class="icon-contact-card mr-3"></i>Add Contact Info</IonTitle>
        <IonButtons slot="end">
          <IconButton icon="icon-x" @click="dismiss()"></IconButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <IonList inset>
        <StackedTextInput
          v-model="name.value"
          :errors="name.errors"
          :state="name.state"
          icon="icon-align-justify"
          gray
          title="Name"
        ></StackedTextInput>
        <StackedTextInput
          v-model="title.value"
          :errors="title.errors"
          :state="title.state"
          icon="icon-user"
          gray
          title="Title"
        ></StackedTextInput>
        <StackedTextInput
          v-model="email.value"
          :errors="email.errors"
          :state="email.state"
          type="email"
          icon="icon-mail"
          gray
          title="Email"
        ></StackedTextInput>
        <StackedTextInput
          v-model="phone.value"
          :errors="phone.errors"
          :state="phone.state"
          type="tel"
          icon="icon-phone"
          gray
          title="Phone"
        ></StackedTextInput>
      </IonList>
    </IonContent>
    <IonFooter class="ion-no-border p-[10px]">
      <IonToolbar color="white" class="border-t-2 border-gray-300">
        <PrimaryBlockButton @click="save()" :disabled="!canSave || saving">
          <IonSpinner name="dots" color="white" v-if="saving"></IonSpinner>
          <span v-else>Save Contact Info</span>
        </PrimaryBlockButton>
      </IonToolbar>
    </IonFooter>
  </IonModal>
</template>

<script setup lang="ts">
import IconButton from '@/components/forms/IconButton.vue';
import LightBlockButton from '@/components/forms/LightBlockButton.vue';
import PrimaryBlockButton from '@/components/forms/PrimaryBlockButton.vue';
import StackedTextInput from '@/components/forms/stacked/StackedTextInput.vue';
import { useFormControl } from '@/composables/use-form-control';
import { useToast } from '@/composables/use-toast';
import { EmailValidator } from '@/composables/validators/account-validators';
import { EmojiValidator } from '@/composables/validators/emoji-validator';
import { LettersOnlyValidator } from '@/composables/validators/letters-only-validator';
import { MaxLengthValidator } from '@/composables/validators/max-length';
import { NoSpecialCharactersValidator } from '@/composables/validators/no-special-characters-validator';
import { PhoneNumberValidator } from '@/composables/validators/phonenumber';
import { useSelectedProperty } from '@/stores/selected-property.store';
import {
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonList,
  IonModal,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/vue';
import { IPropertyContactInfo } from '@ncss/models';
import { computed, ref, useTemplateRef } from 'vue';

withDefaults(
  defineProps<{
    mode?: 'edit' | 'add';
  }>(),
  { mode: 'add' }
);

const propertyStore = useSelectedProperty();
const contactInfoModal = useTemplateRef('contactInfoModal');
const { successToast, errorToast } = useToast();

const name = useFormControl(() => propertyStore.property?.contactInfo?.name, {
  validators: [EmojiValidator(), LettersOnlyValidator(), MaxLengthValidator(50)],
});
const title = useFormControl(() => propertyStore.property?.contactInfo?.title, {
  validators: [EmojiValidator(), NoSpecialCharactersValidator(), MaxLengthValidator(50)],
});
const phone = useFormControl(() => propertyStore.property?.contactInfo?.phone, {
  validators: [PhoneNumberValidator(), EmojiValidator(), NoSpecialCharactersValidator(), MaxLengthValidator(50)],
});
const email = useFormControl(() => propertyStore.property?.contactInfo?.email, {
  validators: [EmailValidator(false), EmojiValidator(), NoSpecialCharactersValidator(), MaxLengthValidator(50)],
});

const dirty = computed(() => name.dirty || title.dirty || phone.dirty || email.dirty);
const valid = computed(() => name.valid && title.valid && phone.valid && email.valid);

const canSave = computed(() => dirty.value && valid.value);
const saving = ref(false);

function dismiss() {
  name.reset();
  title.reset();
  phone.reset();
  email.reset();
  contactInfoModal.value?.$el?.dismiss();
}

async function save() {
  if (canSave.value) {
    saving.value = true;
    const info: IPropertyContactInfo = {
      name: name.value,
      email: email.value,
      phone: phone.value,
      title: title.value,
    };
    const { success, error } = await propertyStore.updateContactInfo(info);
    if (success) {
      successToast('Saved contact info!');
      contactInfoModal.value?.$el?.dismiss();
    } else if (error) {
      errorToast(error);
    }
    saving.value = false;
  }
}
</script>

<style scoped></style>
